/**
 * AudioManager.js - Premium Audio System
 * Gère les sons avec pitch variation, volume global et unlock au premier tap
 */
class AudioManager {
  constructor() {
    this.enabled = true;
    this.unlocked = false;
    this.volume = 0.3; // Volume global (0-1)
    this.sounds = new Map();
    this.context = null;
    
    // Configuration des sons avec leurs chemins
    this.soundPaths = {
      tap: '/audio/tap.mp3',
      whoosh: '/audio/whoosh.mp3',
      flip: '/audio/flip.mp3',
      sparkle: '/audio/sparkle.mp3',
      score: '/audio/score.mp3',
      win: '/audio/win.mp3',
      draw: '/audio/draw.mp3',
      play: '/audio/play.mp3'
    };
    
    // Fallback vers sons générés si fichiers absents
    this.useFallback = false;

    // 🎵 Musique de fond (BGM)
    // - Démarre uniquement après unlock (user gesture)
    // - Playlist simple (enchaînée) pour varier pendant une partie
    this.bgmEnabled = true;
    this.bgmVolume = 0.18; // indépendant des SFX
    this.bgmPlaylist = ['/audio/bgm1.mp3', '/audio/bgm2.mp3'];
    this._bgmIndex = 0;
    this._bgmEl = null;
  }
  
  /**
   * Initialise l'AudioContext et charge les sons
   */
  async init() {
    try {
      // L'AudioContext doit être créé après un geste utilisateur (autoplay policy).
      // On charge les sons au moment du unlock().
      
      console.log('[Audio] AudioManager initialized');
    } catch (error) {
      console.warn('[Audio] Init failed, using fallback:', error);
      this.useFallback = true;
    }
  }
  
  /**
   * Charge tous les sons définis
   */
  async loadAllSounds() {
    const promises = Object.entries(this.soundPaths).map(async ([key, path]) => {
      try {
        const buffer = await this.loadSound(path);
        this.sounds.set(key, buffer);
      } catch (error) {
        console.warn(`[Audio] Failed to load ${key}, will use fallback`);
        this.useFallback = true;
      }
    });
    
    await Promise.all(promises);
  }
  
  /**
   * Charge un fichier audio et retourne un AudioBuffer
   */
  async loadSound(url) {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await this.context.decodeAudioData(arrayBuffer);
  }
  
  /**
   * Unlock audio (doit être appelé sur un user gesture)
   */

  unlock() {
    if (this.unlocked) return;

    try{
      // Créer l'AudioContext au premier geste utilisateur (autoplay policy)
      if(!this.context){
        this.context = new (window.AudioContext || window.webkitAudioContext)();
        // Charger/décoder les sons à ce moment-là (sans bloquer l'UI)
        Promise.resolve()
          .then(()=> this.loadAllSounds())
          .catch((e)=> console.warn('[Audio] loadAllSounds failed:', e));
      }

      if (this.context && this.context.state === 'suspended') {
        this.context.resume();
      }
    }catch(e){
      console.warn('[Audio] Unlock failed:', e);
      this.useFallback = true;
    }

    this.unlocked = true;
    console.log('[Audio] Unlocked');

    // Démarrer la musique de fond après le premier geste utilisateur
    try { this.startBgm(); } catch (_e) {}
  }

  /**
   * Démarre la musique de fond (si disponible). Safe: ne bloque jamais le jeu.
   */
  startBgm() {
    try {
      if (!this.bgmEnabled) return;
      if (this._bgmEl) return;
      if (!Array.isArray(this.bgmPlaylist) || this.bgmPlaylist.length === 0) return;

      const el = new Audio(this.bgmPlaylist[this._bgmIndex] || this.bgmPlaylist[0]);
      el.preload = 'auto';
      el.loop = false; // playlist
      el.volume = Math.max(0, Math.min(1, this.bgmVolume));

      el.addEventListener('ended', () => {
        try {
          if (!this.bgmEnabled) return;
          this._bgmIndex = (this._bgmIndex + 1) % this.bgmPlaylist.length;
          el.src = this.bgmPlaylist[this._bgmIndex];
          el.play().catch(() => {});
        } catch (_e) {}
      });

      // Si fichier introuvable, on désactive proprement.
      el.addEventListener('error', () => {
        console.warn('[Audio] BGM failed to load, disabling BGM');
        this.bgmEnabled = false;
      });

      this._bgmEl = el;
      el.play().catch(() => {
        // Si le navigateur refuse (rare après unlock), on laisse silencieux.
      });
    } catch (_e) {
      // no-op
    }
  }
  
  /**
   * Joue un son avec variation de pitch optionnelle
   * @param {string} soundName - Nom du son à jouer
   * @param {Object} options - Options (volume, pitch variation)
   */
  play(soundName, options = {}) {
    if (!this.enabled || !this.unlocked) return;
    
    try {
      if (this.useFallback) {
        this.playFallback(soundName);
        return;
      }
      
      const buffer = this.sounds.get(soundName);
      if (!buffer) {
        console.warn(`[Audio] Sound not found: ${soundName}`);
        return;
      }
      
      const source = this.context.createBufferSource();
      const gainNode = this.context.createGain();
      
      source.buffer = buffer;
      
      // Volume
      const vol = options.volume !== undefined ? options.volume : 1;
      gainNode.gain.value = this.volume * vol;
      
      // Pitch variation (±3% par défaut)
      const pitchVariation = options.pitchVariation !== undefined ? options.pitchVariation : 0.03;
      const pitchOffset = (Math.random() - 0.5) * 2 * pitchVariation;
      source.playbackRate.value = 1 + pitchOffset;
      
      // Connection
      source.connect(gainNode);
      gainNode.connect(this.context.destination);
      
      // Play
      source.start(0);
    } catch (error) {
      console.warn('[Audio] Play error:', error);
    }
  }
  
  /**
   * Fallback avec sons synthétiques pour développement
   */
  playFallback(soundName) {
    if (!this.context) return;
    
    try {
      const osc = this.context.createOscillator();
      const gain = this.context.createGain();
      
      // Fréquences basées sur le type de son
      const frequencies = {
        tap: 800,
        whoosh: 400,
        flip: 600,
        sparkle: 1200,
        score: 1000,
        win: 1500,
        draw: 700,
        play: 900
      };
      
      osc.frequency.value = frequencies[soundName] || 800;
      osc.type = soundName === 'sparkle' ? 'sine' : 'square';
      
      gain.gain.value = this.volume * 0.1;
      gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.15);
      
      osc.connect(gain);
      gain.connect(this.context.destination);
      
      osc.start();
      osc.stop(this.context.currentTime + 0.15);
    } catch (error) {
      console.warn('[Audio] Fallback error:', error);
    }
  }
  
  /**
   * Change le volume global
   */
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
  }
  
  /**
   * Active/désactive les sons
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }
  
  /**
   * Retourne l'état actuel
   */
  getState() {
    return {
      enabled: this.enabled,
      unlocked: this.unlocked,
      volume: this.volume,
      useFallback: this.useFallback
    };
  }
}

// Export global
window.AudioManager = AudioManager;
